/**
 * Market Preview Component — Figma state "preview".
 *
 * Pill-shaped surface shown once a recommendation resolves, under an
 * `AdvertisementLabel` header. Two interactive elements:
 *   • CTA (right side)  — `Yes/No {probability}%` for binary markets, or
 *                          "Details" for multi-choice. Opens the card.
 *   • Close (top-right) — 24px circle. Collapses to the pill. It is the last
 *                          item in the header row, which is the band it used to
 *                          float in, so the hairline stops short of it.
 *
 * The inline variant keeps the close button so the Preview ↔ Pill cycle
 * works the same as floating, even though the Figma "Widget Embedded"
 * master doesn't depict an inline pill or close affordance — documented
 * design deviation, accepted to give partners a way to collapse the
 * widget out of the article flow.
 *
 * The container itself is not clickable so the nested buttons stay valid.
 * Image slot is decorative (alt=""); the title is the authoritative label.
 */

import type { JSX } from "preact";
import type { IMarketEventDTO } from "../api";
import { getConfig } from "../config";
import { useAnimationVisibility } from "../hooks";
import { messages } from "../i18n";
import {
  floatingPositionClassByPosition,
  previewSurfaceShellClasses,
  surfaceAnimationClass,
  surfaceClickTargetClass,
  WidgetPosition,
  widgetSurfaceWidthClass,
} from "../utils/constants";
import {
  getDominantOutcome,
  getMarketImageSrc,
  isMultiChoice,
} from "../utils/market-event-helpers";
import { AdvertisementLabel } from "./advertisement-label";
import { CloseIcon } from "./close-icon";
import { MarketImage } from "./market-image";

export interface MarketPreviewProps {
  marketEvent: IMarketEventDTO;
  visible: boolean;
  onExpand: () => void;
  onDismiss: () => void;
}

const previewTitleId = "pw-preview-title";

export function MarketPreview({
  marketEvent,
  visible,
  onExpand,
  onDismiss,
}: MarketPreviewProps): JSX.Element | null {
  const { shouldRender, isExiting } = useAnimationVisibility(visible);

  if (!shouldRender) return null;

  const m = messages();
  const position = getConfig().position;
  const animationClass = surfaceAnimationClass(position, isExiting);
  const positionClass = floatingPositionClassByPosition[position];
  const widthClass = widgetSurfaceWidthClass(position, "preview");
  const isInline = position === WidgetPosition.Inline;

  const multiChoice = isMultiChoice(marketEvent);
  const { outcome: dominantOutcome, probability: dominantProbability } =
    getDominantOutcome(marketEvent);

  const ctaLabel = multiChoice
    ? m.preview.ctaMultiChoice
    : m.outcomes[dominantOutcome];

  // While exiting inline, overlay absolutely so the stage measures only the
  // incoming surface (no stack-and-jump) — see `InlineStage` in widget-app.
  const inlineOverlay = isInline && isExiting;

  // Position / width / animation live on the wrapper so the advertisement
  // header travels with the surface. The surface itself keeps chrome only.
  //
  // The `aside` landmark is the wrapper rather than the chrome, so the
  // disclosure and the close button are inside it — a screen-reader user
  // navigating to the landmark would otherwise skip both. Same reasoning as
  // the card's dialog role.
  const wrapperClasses = [
    inlineOverlay
      ? "pw:absolute pw:inset-x-0 pw:top-0 pw:mx-auto"
      : positionClass,
    widthClass,
    animationClass,
    "pw:font-sans",
  ].join(" ");

  // The close button is the label row's trailing item rather than an absolutely
  // positioned overlay. It previously floated at `top:-29px right:1px` — wholly
  // inside the band the header now occupies — so placing it in flow lands it in
  // the same corner while the row's flex gap keeps the hairline clear of it.
  // This also retires the `pw:relative` that only existed to anchor it inline.
  //
  // 24px edge to edge: the glyph fills the button, so icon and button match,
  // and the button's height is what sets the shared label band height.
  const closeButton = (
    <button
      aria-label={m.preview.closeAriaLabel}
      className={`pw:h-6 pw:w-6 pw:flex-shrink-0 pw:flex pw:items-center pw:justify-center pw:rounded-full pw:bg-widget-overlay pw:backdrop-blur-widget pw:text-type-primary-foreground pw:cursor-pointer ${surfaceClickTargetClass}`}
      onClick={onDismiss}
      type="button"
    >
      <CloseIcon className="pw:h-6 pw:w-6" />
    </button>
  );

  return (
    <aside aria-labelledby={previewTitleId} className={wrapperClasses}>
      <AdvertisementLabel trailing={closeButton} />

      <div
        className={`${previewSurfaceShellClasses} ${surfaceClickTargetClass} pw:w-full`}
      >
        {/* Left: market image + title/subtitle. Grows to fill remaining space
            so the text truncates rather than pushing the CTA off-screen. */}
        <div className="pw:flex pw:items-center pw:gap-2 pw:flex-1 pw:min-w-0 pw:h-full">
          {/* Same image resolution as the card header, so the preview and the
              expanded card never disagree on which picture the market has. */}
          <div className="pw:h-16 pw:w-16 pw:flex-shrink-0 pw:rounded-full pw:overflow-hidden pw:bg-type-muted-foreground">
            <MarketImage
              className="pw:w-full pw:h-full pw:object-cover"
              src={getMarketImageSrc(marketEvent)}
            />
          </div>

          <div className="pw:flex pw:flex-col pw:flex-1 pw:min-w-0">
            <h3
              className="pw:text-[1.0625rem] pw:font-bold pw:leading-6 pw:text-type-primary-foreground pw:line-clamp-2"
              id={previewTitleId}
            >
              {marketEvent.title}
            </h3>
            {/* Figma "Label/Small": 11px / 16px, weight 400, 0.03px tracking. */}
            <p className="pw:text-[0.6875rem] pw:font-normal pw:leading-4 pw:tracking-[0.003em] pw:text-type-primary-foreground pw:truncate">
              {m.preview.subtitle}
            </p>
          </div>
        </div>

        {/* CTA — dynamic: binary shows "Yes/No N%" (whichever side leads),
            multi-choice shows "Details". */}
        <button
          aria-label={m.preview.ctaAriaLabel}
          className="pw:flex pw:items-center pw:gap-1 pw:h-12 pw:px-4 pw:flex-shrink-0 pw:rounded-full pw:bg-button-secondary pw:text-button-secondary-foreground pw:font-medium pw:text-[0.9375rem] pw:leading-5 pw:whitespace-nowrap pw:cursor-pointer"
          onClick={onExpand}
          type="button"
        >
          <span>{ctaLabel}</span>
          {!multiChoice && <span>{dominantProbability}%</span>}
        </button>
      </div>
    </aside>
  );
}
